import React, { useEffect, useState } from "react";
import Table from "../../components/Table";
import MultiSelectInput from "../../components/Select/MultiSelectInput";
import { useAuth } from "../../context/AuthContext";
import { getPackages } from "../../services/packagesService";
import { deleteSubscription, getSubscriptions, notifySubscription, SubscriptionScope } from "../../services/renewService";
import { showErrorToast, showSuccessToast } from "../../services/toastService";
import { hasPermission } from "../../utils/permissions";

type PrivateSubscriptionRow = {
    _id: string;
    member?: {
        firstName?: string;
        lastName?: string;
    };
    package?: {
        name?: string;
        numberOfPtSessions?: number;
    };
    startDate?: string;
    expirationDate?: string;
    status?: boolean;
    notify?: boolean;
    price?: number;
    numberofSessions?: number;
    numberOfSessions?: number;
    remainingSessions?: number;
};

type SubscriptionPageState = {
    rows: PrivateSubscriptionRow[];
    page: number;
    total: number;
    loading: boolean;
};

const PAGE_SIZE = 10;

const emptyPageState: SubscriptionPageState = {
    rows: [],
    page: 1,
    total: 0,
    loading: false,
};

const readRows = (response: unknown): PrivateSubscriptionRow[] => {
    if (Array.isArray(response)) return response as PrivateSubscriptionRow[];
    if (!response || typeof response !== "object") return [];

    const payload = response as Record<string, unknown>;
    if (Array.isArray(payload.data)) return payload.data as PrivateSubscriptionRow[];
    if (Array.isArray(payload.subscriptions)) return payload.subscriptions as PrivateSubscriptionRow[];
    if (Array.isArray(payload.items)) return payload.items as PrivateSubscriptionRow[];

    return [];
};

const readTotal = (response: unknown, fallback: number): number => {
    if (!response || typeof response !== "object") return fallback;

    const payload = response as Record<string, unknown>;
    const total = payload.total ?? payload.count ?? payload.totalCount;

    if (typeof total === "number" && Number.isFinite(total)) return total;
    if (typeof total === "string") {
        const parsed = Number(total);
        if (Number.isFinite(parsed)) return parsed;
    }

    return fallback;
};

const PrivateSubscriptions = () => {
    const { user } = useAuth();
    const canEditSubscriptions = hasPermission(user, "subscriptions.write");
    const [thisMonth, setThisMonth] = useState<SubscriptionPageState>(emptyPageState);
    const [oldSubscriptions, setOldSubscriptions] = useState<SubscriptionPageState>(emptyPageState);
    const [thisMonthSearch, setThisMonthSearch] = useState("");
    const [oldSearch, setOldSearch] = useState("");
    const [packageFilter, setPackageFilter] = useState<string[]>([]);
    const [packages, setPackages] = useState<any[]>([]);
    const [notifyingId, setNotifyingId] = useState<string | null>(null);
    const [deletingId, setDeletingId] = useState<string | null>(null);

    const formatDate = (value?: string) => {
        if (!value) return "-";
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return "-";
        return d.toISOString().split("T")[0];
    };

    const formatMemberName = (row?: PrivateSubscriptionRow) => {
        const firstName = row?.member?.firstName ?? "";
        const lastName = row?.member?.lastName ?? "";
        return [firstName, lastName].filter(Boolean).join(" ") || "-";
    };

    const formatSessionCount = (value?: number) =>
        typeof value === "number" && Number.isFinite(value) ? value : "-";

    const getSessionCount = (row: PrivateSubscriptionRow) =>
        row.numberofSessions ?? row.numberOfSessions ?? row.package?.numberOfPtSessions;

    const packageOptions = packages.map((pkg) => ({
        value: pkg._id,
        label: pkg.name,
    }));

    const updateNotifiedRow = (subscriptionId: string) => {
        const markRow = (row: PrivateSubscriptionRow) =>
            row?._id === subscriptionId ? { ...row, notify: true } : row;

        setThisMonth((prev) => ({ ...prev, rows: prev.rows.map(markRow) }));
        setOldSubscriptions((prev) => ({ ...prev, rows: prev.rows.map(markRow) }));
    };

    const removeDeletedRow = (subscriptionId: string) => {
        const removeRow = (row: PrivateSubscriptionRow) => row?._id !== subscriptionId;
        setThisMonth((prev) => ({
            ...prev,
            rows: prev.rows.filter(removeRow),
            total: Math.max(prev.total - 1, 0),
        }));
        setOldSubscriptions((prev) => ({
            ...prev,
            rows: prev.rows.filter(removeRow),
            total: Math.max(prev.total - 1, 0),
        }));
    };

    const handleNotify = async (subscriptionId: string) => {
        try {
            setNotifyingId(subscriptionId);
            await notifySubscription(subscriptionId, true);
            updateNotifiedRow(subscriptionId);
        } catch (err) {
            console.error("Failed to send notification", err);
            showErrorToast("Failed to notify member");
        } finally {
            setNotifyingId(null);
        }
    };

    const handleDelete = async (subscriptionId: string) => {
        if (!subscriptionId) return;
        const confirmed = window.confirm("Delete this subscription?");
        if (!confirmed) return;

        try {
            setDeletingId(subscriptionId);
            await deleteSubscription(subscriptionId);
            removeDeletedRow(subscriptionId);
            showSuccessToast("Subscription deleted");
        } catch (err) {
            console.error("Failed to delete subscription", err);
            showErrorToast("Failed to delete subscription");
        } finally {
            setDeletingId(null);
        }
    };

    const fetchPage = async (
        scope: SubscriptionScope,
        page: number,
        search: string,
        setState: React.Dispatch<React.SetStateAction<SubscriptionPageState>>
    ) => {
        if (!user?.id) return;

        try {
            setState((prev) => ({ ...prev, loading: true }));
            const response = await getSubscriptions({
                scope,
                page,
                limit: PAGE_SIZE,
                search,
                type: "private",
                packageId: packageFilter,
            });
            const rows = readRows(response);
            setState({
                rows,
                page,
                total: readTotal(response, rows.length),
                loading: false,
            });
        } catch (err) {
            console.error(`Failed to fetch private ${scope} subscriptions`, err);
            showErrorToast("Failed to fetch private subscriptions");
            setState((prev) => ({ ...prev, loading: false }));
        }
    };

    useEffect(() => {
        if (!user?.id) return;
        fetchPage("thisMonth", 1, thisMonthSearch, setThisMonth);
    }, [user?.id, thisMonthSearch, packageFilter]);

    useEffect(() => {
        if (!user?.id) return;
        fetchPage("old", 1, oldSearch, setOldSubscriptions);
    }, [user?.id, oldSearch, packageFilter]);

    useEffect(() => {
        const fetchPackages = async () => {
            if (!user?.id) return;

            try {
                const response = await getPackages();
                setPackages(Array.isArray(response) ? response : []);
            } catch (err) {
                console.error("Failed to fetch packages", err);
                showErrorToast("Failed to fetch packages");
            }
        };

        fetchPackages();
    }, [user?.id]);

    const StatusCell = ({ value }: { value: boolean }) => (
        <span className={`px-2 py-1 rounded-md ${value ? "text-green-500" : "text-red-500"}`}>
            {value ? "Active" : "Inactive"}
        </span>
    );

    const NotifyCell = ({ value }: { value?: boolean }) => (
        <span className={`px-2 py-1 rounded-md ${value ? "text-green-600" : "text-gray-600"}`}>
            {value ? "Yes" : "No"}
        </span>
    );

    const columns = [
        {
            Header: "Member",
            accessor: "member",
            Cell: ({ row }: { row: { original: PrivateSubscriptionRow } }) => formatMemberName(row.original),
        },
        {
            Header: "Package",
            accessor: "package",
            Cell: ({ row }: { row: { original: PrivateSubscriptionRow } }) => row.original.package?.name ?? "-",
        },
        {
            Header: "Sessions",
            accessor: "numberofSessions",
            Cell: ({ row }: { row: { original: PrivateSubscriptionRow } }) => formatSessionCount(getSessionCount(row.original)),
        },
        {
            Header: "Remaining",
            accessor: "remainingSessions",
            Cell: ({ value }: { value?: number }) => formatSessionCount(value),
        },
        { Header: "Start Date", accessor: "startDate", Cell: ({ value }: { value?: string }) => formatDate(value) },
        { Header: "End Date", accessor: "expirationDate", Cell: ({ value }: { value?: string }) => formatDate(value) },
        { Header: "Status", accessor: "status", Cell: StatusCell },
        { Header: "Notify", accessor: "notify", Cell: NotifyCell },
        { Header: "Price", accessor: "price" },
        ...(canEditSubscriptions
            ? [{
                Header: "Actions",
                accessor: "actions",
                Cell: ({ row }: { row: { original: PrivateSubscriptionRow } }) => {
                const subscriptionId = row?.original?._id;
                const alreadyNotified = Boolean(row?.original?.notify);
                const isNotifying = notifyingId === subscriptionId;
                const isDeleting = deletingId === subscriptionId;

                return (
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={() => handleNotify(subscriptionId)}
                            disabled={!subscriptionId || alreadyNotified || isNotifying || isDeleting}
                            className="px-3 py-1 rounded-md bg-[#ac120c] hover:bg-[#90100b] transition text-white text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {alreadyNotified ? "Notified" : isNotifying ? "Sending..." : "Notify"}
                        </button>
                        <button
                            type="button"
                            onClick={() => handleDelete(subscriptionId)}
                            disabled={!subscriptionId || isDeleting || isNotifying}
                            className="px-3 py-1 rounded-md bg-gray-800 hover:bg-black transition text-white text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isDeleting ? "Deleting..." : "Delete"}
                        </button>
                    </div>
                );
                },
            }]
            : []),
    ];

    const renderPagination = (
        state: SubscriptionPageState,
        scope: SubscriptionScope,
        setState: React.Dispatch<React.SetStateAction<SubscriptionPageState>>
    ) => {
        const totalPages = Math.max(Math.ceil(state.total / PAGE_SIZE), 1);

        return (
            <div className="mt-4 flex items-center justify-between">
                <button
                    type="button"
                    onClick={() => fetchPage(scope, state.page - 1, scope === "thisMonth" ? thisMonthSearch : oldSearch, setState)}
                    disabled={state.loading || state.page <= 1}
                    className="px-4 py-2 bg-gray-800 text-white rounded disabled:opacity-50"
                >
                    Previous
                </button>
                <span className="text-sm">
                    Page {state.page} of {totalPages}
                </span>
                <button
                    type="button"
                    onClick={() => fetchPage(scope, state.page + 1, scope === "thisMonth" ? thisMonthSearch : oldSearch, setState)}
                    disabled={state.loading || state.page >= totalPages}
                    className="px-4 py-2 bg-gray-800 text-white rounded disabled:opacity-50"
                >
                    Next
                </button>
            </div>
        );
    };

    return (
        <div className="w-full space-y-8">
            <section>
                <div className="mb-3 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                    <div>
                        <h2 className="text-lg font-semibold">This Month Private Subscriptions</h2>
                        {thisMonth.loading && <span className="text-sm text-gray-500">Loading...</span>}
                    </div>
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                        <MultiSelectInput
                            label="Packages"
                            name="packageFilter"
                            value={packageFilter}
                            options={packageOptions}
                            placeholder="All packages"
                            onChange={setPackageFilter}
                        />
                        <input
                            type="search"
                            value={thisMonthSearch}
                            onChange={(event) => setThisMonthSearch(event.target.value)}
                            placeholder="Search this month..."
                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm sm:w-64"
                        />
                    </div>
                </div>
                <Table
                    columns={columns}
                    data={thisMonth.rows}
                    pageSize={PAGE_SIZE}
                    add={false}
                    renew={false}
                    checkbox={true}
                    showSearch={false}
                    showPagination={false}
                />
                {renderPagination(thisMonth, "thisMonth", setThisMonth)}
            </section>

            <section>
                <div className="mb-3 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                    <div>
                        <h2 className="text-lg font-semibold">Old Private Subscriptions</h2>
                        {oldSubscriptions.loading && <span className="text-sm text-gray-500">Loading...</span>}
                    </div>
                    <input
                        type="search"
                        value={oldSearch}
                        onChange={(event) => setOldSearch(event.target.value)}
                        placeholder="Search old subscriptions..."
                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm sm:w-64"
                    />
                </div>
                <Table
                    columns={columns}
                    data={oldSubscriptions.rows}
                    pageSize={PAGE_SIZE}
                    add={false}
                    renew={false}
                    checkbox={true}
                    showSearch={false}
                    showPagination={false}
                />
                {renderPagination(oldSubscriptions, "old", setOldSubscriptions)}
            </section>
        </div>
    );
};

export default PrivateSubscriptions;
